// src/modulos/Dashboard/dashboards/LogisticaDashboard.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db } from '../../firebaseConfig';
import {
    collection,
    query,
    orderBy,
    limit,
    onSnapshot,
    where,
    getDocs,
    doc,
    getDoc
} from 'firebase/firestore';
import useAuth from '../../hooks/useAuth';
import dashboardStyles from './Dashboard.module.css';

// Professional SVG Icons (consistent with TIDashboard)
const Icons = {
    Package: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16.5 9.4 7.55 4.24" />
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.29 7 12 12 20.71 7" />
            <line x1="12" y1="22" x2="12" y2="12" />
        </svg>
    ),
    Truck: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="3" width="15" height="13" />
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
            <circle cx="5.5" cy="18.5" r="2.5" />
            <circle cx="18.5" cy="18.5" r="2.5" />
        </svg>
    ),
    AlertTriangle: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    Warehouse: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
            <line x1="8" y1="2" x2="8" y2="18" />
            <line x1="16" y1="6" x2="16" y2="22" />
        </svg>
    ),
    Clock: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
        </svg>
    ),
    BarChart: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
    ),
    TrendingUp: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            <polyline points="17 6 23 6 23 12" />
        </svg>
    ),
    TrendingDown: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
            <polyline points="17 18 23 18 23 12" />
        </svg>
    ),
    User: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    ),
    Refresh: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 4v6h-6" />
            <path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
        </svg>
    ),
    CheckCircle: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    ),
    XCircle: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    ),
    Search: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    ),
    Filter: () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" />
        </svg>
    ),
    Download: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
    ),
    ChevronLeft: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    ),
    ChevronRight: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    ),
};

// Componente de Card com estilo consistente (usando CSS modules)
const DashboardCard = ({ title, icon, children, status, statusText, onClear, variant = 'default' }) => (
    <div className={`${dashboardStyles.card} ${dashboardStyles[variant]}`}>
        <div className={dashboardStyles.cardHeader}>
            <div className={dashboardStyles.cardTitle}>
                <div className={dashboardStyles.cardIcon}>{icon}</div>
                <h3>{title}</h3>
            </div>
            <div className={dashboardStyles.cardActions}>
                {onClear && (
                    <button onClick={onClear} className={dashboardStyles.clearBtn}>
                        <Icons.Trash />
                    </button>
                )}
                {status && (
                    <div className={`${dashboardStyles.statusBadge} ${dashboardStyles[status]}`}>
                        <div className={dashboardStyles.statusDot} />
                        {statusText}
                    </div>
                )}
            </div>
        </div>
        <div className={dashboardStyles.cardContent}>{children}</div>
    </div>
);

// Componente de Métricas (usando CSS modules)
const MetricsCard = ({ label, value, subtitle, color, icon, percentage, loading, tooltip, trend }) => (
    <div className={dashboardStyles.metricsCard} title={tooltip}>
        <div className={dashboardStyles.metricsHeader}>
            <div className={dashboardStyles.metricsIcon} style={{ backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
            {percentage !== undefined && !loading && (
                <span className={dashboardStyles.metricsPercentage} style={{ backgroundColor: `${color}15`, color: color }}>
                    {percentage}%
                </span>
            )}
        </div>
        <div className={dashboardStyles.metricsValue}>
            {loading ? '...' : value}
        </div>
        <div className={dashboardStyles.metricsLabel}>{label}</div>
        {subtitle && !loading && (
            <div className={dashboardStyles.metricsSubtitle}>
                <Icons.Clock />
                {subtitle}
            </div>
        )}
        {trend && !loading && (
            <div className={dashboardStyles.metricsTrend}>
                {trend.isPositive ? <Icons.TrendingUp /> : <Icons.TrendingDown />}
                <span className={trend.isPositive ? dashboardStyles.trendUp : dashboardStyles.trendDown}>
                    {trend.value}% vs semana anterior
                </span>
            </div>
        )}
    </div>
);

const LogisticaDashboard = ({ user }) => {
    const { currentUser } = useAuth();
    const USER_EMAIL = currentUser?.email || 'sistema';

    // State for real-time data
    const [stockData, setStockData] = useState([]);
    const [rawMaterials, setRawMaterials] = useState(new Map());
    const [products, setProducts] = useState(new Map());
    const [recentEntries, setRecentEntries] = useState([]);
    const [recentOutflows, setRecentOutflows] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [integrityCheck, setIntegrityCheck] = useState({ status: 'ok', message: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const isMounted = useRef(true);

    // Fetch raw materials with minStock
    useEffect(() => {
        const fetchRawMaterials = async () => {
            try {
                const rmSnapshot = await getDocs(collection(db, 'raw-materials'));
                const map = new Map();

                rmSnapshot.forEach(doc => {
                    const data = doc.data();
                    map.set(doc.id, {
                        id: doc.id,
                        code: data.code,
                        name: data.commercialName,
                        minStock: data.minStock || 500,
                        unit: data.unit || 'un',
                        isProduct: false
                    });
                });

                if (isMounted.current) setRawMaterials(map);
            } catch (error) {
                console.error("Error fetching raw materials:", error);
            }
        };
        fetchRawMaterials();
    }, []);

    // Fetch products with minStock
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const productsSnapshot = await getDocs(collection(db, 'products'));
                const map = new Map();

                productsSnapshot.forEach(doc => {
                    const data = doc.data();
                    map.set(doc.id, {
                        id: doc.id,
                        code: data.code,
                        name: data.commercialName,
                        minStock: data.minStock || 500,
                        unit: data.unit || 'un',
                        isProduct: true
                    });
                });

                if (isMounted.current) setProducts(map);
            } catch (error) {
                console.error("Error fetching products:", error);
            }
        };
        fetchProducts();
    }, []);

    // Optimized stock listener with filter for positive quantity
    useEffect(() => {
        isMounted.current = true;
        setLoading(true);

        const stockQuery = query(
            collection(db, 'current-stock'),
            where('quantity', '>', 0.001)
        );

        const unsubscribe = onSnapshot(stockQuery,
            (snapshot) => {
                if (!isMounted.current) return;
                const stock = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setStockData(stock);
                setLastUpdated(new Date());
                setLoading(false);
            },
            (error) => {
                console.error('Error loading stock:', error);
                if (isMounted.current) setLoading(false);
            }
        );

        return () => {
            isMounted.current = false;
            unsubscribe();
        };
    }, []);

    // Fetch locations once
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const locQuery = query(collection(db, 'localizacoes'), limit(500));
                const locSnap = await getDocs(locQuery);
                const locs = locSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                if (isMounted.current) setLocations(locs);
            } catch (error) {
                console.error('Error fetching locations:', error);
            }
        };

        fetchLocations();
    }, []);

    // Real-time listeners for entries and outflows
    useEffect(() => {
        const entriesQuery = query(
            collection(db, 'stock-entries'),
            orderBy('createdAt', 'desc'),
            limit(30)
        );

        const unsubscribeEntries = onSnapshot(entriesQuery,
            (snapshot) => {
                if (!isMounted.current) return;
                const entries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setRecentEntries(entries);
            },
            (error) => console.error('Error loading entries:', error)
        );

        const outflowsQuery = query(
            collection(db, 'stock-outflows'),
            orderBy('createdAt', 'desc'),
            limit(30)
        );

        const unsubscribeOutflows = onSnapshot(outflowsQuery,
            (snapshot) => {
                if (!isMounted.current) return;
                const outflows = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setRecentOutflows(outflows);
            },
            (error) => console.error('Error loading outflows:', error)
        );

        return () => {
            unsubscribeEntries();
            unsubscribeOutflows();
        };
    }, []);

    // Memoized statistics with integrity check
    const stats = useMemo(() => {
        if (stockData.length === 0 && locations.length === 0) {
            return {
                totalProducts: 0,
                lowStockItems: 0,
                criticalStockItems: 0,
                zeroStockItems: 0,
                stockOccupancy: 0,
                usedLocations: 0,
                totalLocations: locations.length || 0,
                totalQuantity: 0,
                entriesToday: 0,
                outflowsToday: 0,
                totalMovementsToday: 0,
                integrityStatus: { status: 'ok', message: '' },
                topLowStockItems: []
            };
        }

        // Combine raw materials and products
        const allItems = new Map();
        rawMaterials.forEach((value, key) => allItems.set(key, value));
        products.forEach((value, key) => allItems.set(key, value));

        // Group by rawMaterialId
        const productMap = new Map();
        const locationSet = new Set();
        let totalQuantitySum = 0;

        stockData.forEach(item => {
            const key = item.rawMaterialId;
            locationSet.add(item.locationId);
            totalQuantitySum += item.quantity;

            if (!productMap.has(key)) {
                const itemInfo = allItems.get(key) || {};
                productMap.set(key, {
                    id: key,
                    code: item.rawMaterialCode,
                    name: item.rawMaterialName,
                    totalQuantity: 0,
                    minStock: itemInfo.minStock || 500,
                    unit: itemInfo.unit || 'un'
                });
            }
            const prod = productMap.get(key);
            prod.totalQuantity += item.quantity;
        });

        const products_list = Array.from(productMap.values());
        const totalProducts = products_list.length;

        // Stock level calculations
        const lowStockItems = products_list.filter(p => p.totalQuantity < p.minStock && p.totalQuantity > 0).length;
        const criticalStockItems = products_list.filter(p => p.totalQuantity < (p.minStock / 2) && p.totalQuantity > 0).length;
        const zeroStockItems = products_list.filter(p => p.totalQuantity === 0).length;

        // Get top 5 low stock items for alert list
        const topLowStockItems = products_list
            .filter(p => p.totalQuantity < p.minStock)
            .sort((a, b) => (a.totalQuantity / a.minStock) - (b.totalQuantity / b.minStock))
            .slice(0, 5)
            .map(p => ({ ...p, usagePercentage: Math.round((p.totalQuantity / p.minStock) * 100) }));

        // Warehouse occupancy
        const totalLocations = locations.length;
        const usedLocations = locationSet.size;
        const stockOccupancy = totalLocations > 0 ? Math.round((usedLocations / totalLocations) * 100) : 0;

        // Today's movements
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const entriesToday = recentEntries.filter(e => {
            const entryDate = e.createdAt?.toDate();
            return entryDate && entryDate >= today;
        }).length;

        const outflowsToday = recentOutflows.filter(o => {
            const outflowDate = o.createdAt?.toDate();
            return outflowDate && outflowDate >= today;
        }).length;

        // Integrity check: verify if there are movements but no stock
        let integrityStatus = { status: 'ok', message: '' };
        if ((entriesToday + outflowsToday) > 0 && totalProducts === 0) {
            integrityStatus = {
                status: 'warning',
                message: 'Movimentações registradas sem estoque atual. Verifique consistência dos dados.'
            };
        } else if (totalProducts === 0 && stockData.length > 0) {
            integrityStatus = {
                status: 'warning',
                message: 'Estoque registrado mas sem produtos cadastrados.'
            };
        }

        return {
            totalProducts,
            lowStockItems,
            criticalStockItems,
            zeroStockItems,
            stockOccupancy,
            usedLocations,
            totalLocations,
            totalQuantity: totalQuantitySum,
            entriesToday,
            outflowsToday,
            totalMovementsToday: entriesToday + outflowsToday,
            integrityStatus,
            topLowStockItems
        };
    }, [stockData, rawMaterials, products, locations, recentEntries, recentOutflows]);

    // Memoized recent movements with search filter
    const recentMovements = useMemo(() => {
        const movements = [
            ...recentEntries.map(e => ({
                ...e,
                movementType: e.type === 'TRANSFER_IN' ? 'Transferência Entrada' : 'Entrada',
                isInbound: true,
            })),
            ...recentOutflows.map(o => ({
                ...o,
                movementType: o.type === 'TRANSFER_OUT' ? 'Transferência Saída' : 'Saída',
                isInbound: false,
            }))
        ];

        let filtered = movements
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

        // Apply search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(mov =>
                mov.rawMaterialCode?.toLowerCase().includes(term) ||
                mov.rawMaterialName?.toLowerCase().includes(term) ||
                mov.movementType?.toLowerCase().includes(term) ||
                mov.notes?.toLowerCase().includes(term)
            );
        }

        return filtered;
    }, [recentEntries, recentOutflows, searchTerm]);

    // Pagination
    const totalPages = Math.ceil(recentMovements.length / itemsPerPage);
    const paginatedMovements = recentMovements.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate();
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatQuantity = (quantity) => {
        const num = Number(quantity);
        if (isNaN(num)) return '0';
        return num.toLocaleString('pt-BR', {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
        });
    };

    const exportToCSV = () => {
        const headers = ['Data', 'Tipo', 'Código', 'Material', 'Quantidade', 'Observação'];
        const rows = recentMovements.map(mov => [
            formatTimestamp(mov.createdAt),
            mov.movementType,
            mov.rawMaterialCode,
            mov.rawMaterialName,
            `${mov.isInbound ? '+' : '-'}${formatQuantity(mov.quantity)}`,
            mov.notes || ''
        ]);

        const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute('download', `movimentacoes_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (loading && stockData.length === 0) {
        return (
            <div className={dashboardStyles.loadingContainer}>
                <div className={dashboardStyles.spinner} />
                <p>Carregando dashboard logístico...</p>
            </div>
        );
    }

    return (
        <div className={dashboardStyles.dashboardContainer}>
            {/* Header with integrity indicator */}
            <div className={dashboardStyles.dashboardHeader}>
                <div className={dashboardStyles.headerMeta}>
                    <div className={dashboardStyles.lastUpdate}>
                        <Icons.Clock />
                        Última atualização: {lastUpdated.toLocaleTimeString('pt-BR')}
                    </div>
                    <div className={dashboardStyles.userInfo}>
                        <Icons.User />
                        {user?.nome || USER_EMAIL}
                    </div>
                    {stats.integrityStatus.status !== 'ok' && (
                        <div className={dashboardStyles.statusBadge} style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
                            <div className={dashboardStyles.statusDot} style={{ backgroundColor: '#dc2626' }} />
                            {stats.integrityStatus.message}
                        </div>
                    )}
                    {stats.integrityStatus.status === 'ok' && !loading && (
                        <div className={dashboardStyles.statusBadge} style={{ backgroundColor: '#ecfdf5', color: '#059669' }}>
                            <div className={dashboardStyles.statusDot} style={{ backgroundColor: '#10b981' }} />
                            Dados consistentes
                        </div>
                    )}
                </div>
            </div>

            {/* Overview Metrics */}
            <div className={dashboardStyles.section}>
                <h2 className={dashboardStyles.sectionTitle}>Visão Geral do Estoque</h2>
                <div className={dashboardStyles.metricsGrid}>
                    <MetricsCard
                        label="Itens em Estoque"
                        value={stats.totalProducts}
                        color="#3b82f6"
                        icon={<Icons.Package />}
                        loading={loading}
                        tooltip="Total de produtos com estoque registrado"
                    />
                    <MetricsCard
                        label="Estoque Crítico"
                        value={stats.criticalStockItems}
                        color="#ef4444"
                        icon={<Icons.AlertTriangle />}
                        subtitle={`${stats.lowStockItems} baixo / ${stats.zeroStockItems} zerados`}
                        loading={loading}
                        tooltip="Itens abaixo de 50% do estoque mínimo"
                    />
                    <MetricsCard
                        label="Ocupação do Armazém"
                        value={`${stats.stockOccupancy}%`}
                        color="#f59e0b"
                        icon={<Icons.Warehouse />}
                        percentage={stats.stockOccupancy}
                        subtitle={`${stats.usedLocations} de ${stats.totalLocations} posições`}
                        loading={loading}
                        tooltip="Posições de armazenamento ocupadas vs. totais"
                    />
                    <MetricsCard
                        label="Movimentações Hoje"
                        value={stats.totalMovementsToday}
                        color="#10b981"
                        icon={<Icons.TrendingUp />}
                        subtitle={`${stats.entriesToday} entradas / ${stats.outflowsToday} saídas`}
                        loading={loading}
                        tooltip="Total de movimentações nas últimas 24 horas"
                    />
                </div>
            </div>

            {/* Detailed Monitoring Cards */}
            <div className={dashboardStyles.section}>
                <h2 className={dashboardStyles.sectionTitle}>Monitoramento Detalhado</h2>
                <div className={dashboardStyles.healthGrid}>
                    {/* Critical Stock Alert Card */}
                    <DashboardCard
                        title="Alertas de Estoque"
                        icon={<Icons.AlertTriangle />}
                        status={stats.criticalStockItems > 0 ? 'degraded' : (stats.lowStockItems > 0 ? 'operational' : 'operational')}
                        statusText={stats.criticalStockItems > 0 ? 'Urgente' : (stats.lowStockItems > 0 ? 'Atenção' : 'Normal')}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>Abaixo do Mínimo</div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Estoque &lt; mínimo cadastrado</div>
                                </div>
                                <span style={{ fontWeight: '700', color: stats.lowStockItems > 0 ? '#ef4444' : '#10b981', fontSize: '24px' }}>{stats.lowStockItems}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>Estado Crítico</div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Estoque &lt; 50% do mínimo</div>
                                </div>
                                <span style={{ fontWeight: '700', color: stats.criticalStockItems > 0 ? '#dc2626' : '#10b981', fontSize: '24px' }}>{stats.criticalStockItems}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>Sem Estoque</div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Quantidade zerada</div>
                                </div>
                                <span style={{ fontWeight: '700', color: stats.zeroStockItems > 0 ? '#94a3b8' : '#10b981', fontSize: '24px' }}>{stats.zeroStockItems}</span>
                            </div>

                            {/* Top 5 low stock items list */}
                            {stats.topLowStockItems.length > 0 && (
                                <div style={{ marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>⚠️ Itens com estoque baixo:</div>
                                    {stats.topLowStockItems.map(item => (
                                        <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', padding: '4px 0', color: '#64748b' }}>
                                            <span style={{ fontWeight: '500' }}>{item.code}</span>
                                            <span style={{ color: item.usagePercentage < 30 ? '#dc2626' : '#d97706' }}>
                                                {item.totalQuantity.toFixed(1)} / {item.minStock} {item.unit}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </DashboardCard>

                    {/* Warehouse Status Card */}
                    <DashboardCard title="Status do Armazém" icon={<Icons.BarChart />}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Ocupação do Armazém</span>
                                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>{stats.stockOccupancy}%</span>
                                </div>
                                <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                                    <div style={{
                                        width: `${stats.stockOccupancy}%`,
                                        height: '100%',
                                        backgroundColor: stats.stockOccupancy > 80 ? '#ef4444' : stats.stockOccupancy > 60 ? '#f59e0b' : '#10b981',
                                        borderRadius: '4px',
                                        transition: 'width 0.5s ease'
                                    }} />
                                </div>
                            </div>
                            <div style={{ backgroundColor: '#f8fafc', borderRadius: '12px', padding: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>Posições Ocupadas</span>
                                    <span style={{ fontWeight: '700', color: '#3b82f6', fontSize: '18px' }}>{stats.usedLocations}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>Posições Totais</span>
                                    <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '18px' }}>{stats.totalLocations}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>Estoque Total (unidades)</span>
                                    <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '16px' }}>{formatQuantity(stats.totalQuantity)}</span>
                                </div>
                            </div>
                            {stats.stockOccupancy > 85 && (
                                <div style={{ fontSize: '12px', padding: '10px 12px', backgroundColor: '#fef2f2', borderRadius: '8px', color: '#dc2626', textAlign: 'center' }}>
                                    ⚠️ Alta ocupação. Considere expandir ou otimizar espaços.
                                </div>
                            )}
                        </div>
                    </DashboardCard>

                    {/* Movement Summary Card */}
                    <DashboardCard
                        title="Fluxo de Movimentações"
                        icon={<Icons.Truck />}
                        status={stats.totalMovementsToday > 30 ? 'degraded' : 'operational'}
                        statusText={stats.totalMovementsToday > 30 ? 'Alta atividade' : 'Normal'}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>Total Movimentações</div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Últimas 24 horas</div>
                                </div>
                                <span style={{ fontWeight: '700', color: '#3b82f6', fontSize: '28px' }}>{stats.totalMovementsToday}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>Entradas</div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Recebimentos + Transferências</div>
                                </div>
                                <span style={{ fontWeight: '700', color: '#10b981', fontSize: '22px' }}>+{stats.entriesToday}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                                <div>
                                    <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>Saídas</div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Consumo + Transferências</div>
                                </div>
                                <span style={{ fontWeight: '700', color: '#ef4444', fontSize: '22px' }}>-{stats.outflowsToday}</span>
                            </div>
                            {stats.totalMovementsToday === 0 && (
                                <div style={{ fontSize: '12px', padding: '10px 12px', backgroundColor: '#f1f5f9', borderRadius: '8px', color: '#64748b', textAlign: 'center' }}>
                                    Nenhuma movimentação registrada hoje.
                                </div>
                            )}
                        </div>
                    </DashboardCard>
                </div>
            </div>

            {/* Recent Movements Table with Search and Pagination */}
            <div className={dashboardStyles.section}>
                <div className={dashboardStyles.sectionHeader}>
                    <h2 className={dashboardStyles.sectionTitle}>Últimas Movimentações</h2>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Buscar movimentação..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{
                                    padding: '8px 12px 8px 32px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    width: '220px',
                                    backgroundColor: '#ffffff'
                                }}
                            />
                            <div style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                <Icons.Search />
                            </div>
                        </div>
                        <button
                            onClick={exportToCSV}
                            className={dashboardStyles.focusModeButton}
                            style={{ backgroundColor: '#10b981', padding: '6px 12px' }}
                        >
                            <Icons.Download /> Exportar
                        </button>
                        <span className={dashboardStyles.sectionBadge}>{recentMovements.length} movimentos</span>
                    </div>
                </div>

                <div className={dashboardStyles.ticketsTable}>
                    {paginatedMovements.length > 0 ? (
                        <>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                            <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px' }}>Data</th>
                                            <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px' }}>Tipo</th>
                                            <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px' }}>Código</th>
                                            <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px' }}>Material</th>
                                            <th style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '600', color: '#475569', fontSize: '12px' }}>Quantidade</th>
                                            <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px' }}>Observação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedMovements.map((mov, index) => (
                                            <tr key={mov.id} style={{ borderBottom: index < paginatedMovements.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                                <td style={{ padding: '14px 20px', color: '#64748b', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                    {formatTimestamp(mov.createdAt)}
                                                    {mov.updatedAt && <span style={{ display: 'block', fontSize: '10px', color: '#94a3b8' }}>(editado)</span>}
                                                </td>
                                                <td style={{ padding: '14px 20px' }}>
                                                    <span style={{
                                                        padding: '4px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        fontWeight: '600',
                                                        backgroundColor: mov.isInbound ? '#ecfdf5' : '#fef2f2',
                                                        color: mov.isInbound ? '#059669' : '#dc2626',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {mov.movementType}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 20px', fontWeight: '500', color: '#1e293b', fontSize: '12px' }}>
                                                    {mov.rawMaterialCode}
                                                </td>
                                                <td style={{ padding: '14px 20px', fontSize: '12px', color: '#475569', maxWidth: '250px' }}>
                                                    <div style={{ fontWeight: '500' }}>
                                                        {mov.rawMaterialName?.length > 40 ? mov.rawMaterialName.substring(0, 40) + '...' : mov.rawMaterialName}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '700', color: mov.isInbound ? '#059669' : '#dc2626', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                    {mov.isInbound ? '+' : '-'}{formatQuantity(mov.quantity)}
                                                </td>
                                                <td style={{ padding: '14px 20px', color: '#94a3b8', fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {mov.notes || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid #f1f5f9', backgroundColor: '#fafbfc' }}>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '6px 12px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            backgroundColor: currentPage === 1 ? '#f1f5f9' : '#ffffff',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            color: currentPage === 1 ? '#94a3b8' : '#475569'
                                        }}
                                    >
                                        <Icons.ChevronLeft /> Anterior
                                    </button>
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                                        Página {currentPage} de {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '6px 12px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            backgroundColor: currentPage === totalPages ? '#f1f5f9' : '#ffffff',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            color: currentPage === totalPages ? '#94a3b8' : '#475569'
                                        }}
                                    >
                                        Próximo <Icons.ChevronRight />
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={dashboardStyles.emptyTable}>
                            <div className={dashboardStyles.emptyIcon}>
                                <Icons.Package />
                            </div>
                            <p>{searchTerm ? 'Nenhuma movimentação encontrada' : 'Nenhuma movimentação registrada'}</p>
                            {searchTerm && <span>Tente outro termo de busca</span>}
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default LogisticaDashboard;